import {
	Attachment,
	AttachmentHeader,
	AttachmentUploadV2
} from "@com.mgmtp.a12.dataservices/dataservices-access/lib/Attachment/attachment";
import { platformAttachmentLoader } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import {
	AttachmentFile,
	AttachmentLoader,
	AttachmentWithThumbnail
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions/internal/extensions/form-engine/internal/attachments/attachmentLoader/AttachmentLoader";
import { DocumentDescriptor } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions/internal/extensions/form-engine/internal/attachments/documentDescriptor/DocumentDescriptor";
import { DocumentPath } from "@com.mgmtp.a12.formengine/formengine-core/lib/models/internal/utils/document-utils";
import { ConnectorLocator, RestServerConnector } from "@com.mgmtp.a12.utils/utils-connector/lib/main";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { RESOURCE_KEYS } from "../localization";
import { getAuthHeaders, getConfig } from "../antragstellungs-client/modulf/antragstellung-api";

const logger = LoggerFactory.getLogger("modulf");
const unacceptableFileExtensions = ["exe", "bat", "com", "msi", "vbs", "cmd"];

abstract class DefaultAttachmentLoader implements AttachmentLoader {
	async uploadFiles(
		files: AttachmentFile[],
		documentDescriptor: DocumentDescriptor,
		abortSignal: AbortSignal
	): Promise<(Localizable | AttachmentWithThumbnail)[]> {
		return await Promise.all(
			files.map(async ({ file, attachmentPath }) => {
				if (!file) {
					return EMPTY_ATTACHMENT;
				}

				const unacceptedFileEnding = unacceptableFileExtensions.find(it => file.name.endsWith(it));
				if (unacceptedFileEnding) {
					return this.createErrorMessage(file);
				}

				const content = await file.arrayBuffer();
				const attachmentUploadRequest = {
					fileName: file.name,
					documentModelName: documentDescriptor.documentModelName,
					content: content,
					pathToField: DocumentPath.toString(attachmentPath)
				};

				return this.sendRequest(attachmentUploadRequest, abortSignal)
					.then(response => {
						if (!response.ok) {
							throw new Error(response.statusText);
						}
						return response.json();
					})
					.then(data => {
						if (!AttachmentHeader.isInstance(data)) {
							throw new Error("The server response cannot be interpreted!");
						}

						const decodedFilename = data.filename ? decodeURIComponent(data.filename) : "";

						return {
							content: null,
							attachment_id: data.attachmentId,
							internal_filename: decodedFilename,
							original_filename: decodedFilename,
							size: data.size,
							mime_type: data.mimeType,
							thumbnail: data.bigThumbnailUrl
						};
					})
					.catch(it => {
						logger.error(it);
						return this.createErrorMessage(file);
					});
			})
		);
	}

	private createErrorMessage(file: File): Localizable {
		const unacceptedFileEnding = unacceptableFileExtensions.find(it => file.name.endsWith(it));
		if (unacceptedFileEnding) {
			return {
				key: RESOURCE_KEYS.resources["attachment-handler"].error.unacceptableExtension,
				args: {
					filename: { type: "plain", value: file.name },
					extension: { type: "plain", value: unacceptedFileEnding }
				}
			};
		} else {
			return {
				key: RESOURCE_KEYS.resources["attachment-handler"].error.unknown,
				args: {
					filename: { type: "plain", value: file.name }
				}
			} as Localizable;
		}
	}

	retrieveDownloadLink(attachment: Attachment, documentDescriptor: DocumentDescriptor): Promise<string> {
		return platformAttachmentLoader.retrieveDownloadLink(attachment, documentDescriptor);
	}

	abstract sendRequest(attachmentUploadRequest: AttachmentUploadV2.Request, signal: AbortSignal): Promise<Response>;
}

export class PortalAttachmentLoader extends DefaultAttachmentLoader {
	sendRequest(attachmentUploadRequest: AttachmentUploadV2.Request, signal: AbortSignal): Promise<Response> {
		const restRequest = AttachmentUploadV2.Request.build(attachmentUploadRequest);
		const request = {
			...restRequest,
			signal,
			customHeaders: [...(restRequest.customHeaders ?? []), ["content-type", "application/octet-stream"]]
		};
		return (ConnectorLocator.getInstance().getServerConnector() as RestServerConnector).fetchData(request);
	}
}

export class InterneAntragsstelleAttachmentLoader extends DefaultAttachmentLoader {
	override async retrieveDownloadLink(
		attachment: Attachment,
		documentDescriptor: DocumentDescriptor
	): Promise<string> {
		return Promise.resolve("");
	}

	sendRequest(attachmentUploadRequest: AttachmentUploadV2.Request, signal: AbortSignal): Promise<Response> {
		const cfg = getConfig();
		return fetch(this.buildAttachmentUploadUrl(cfg.attachmentUploadUrl, attachmentUploadRequest), {
			headers: {
				"Content-Type": "application/json",
				...getAuthHeaders()
			},
			method: "POST",
			body: attachmentUploadRequest.content,
			signal
		});
	}

	buildAttachmentUploadUrl = (url: string, request: AttachmentUploadV2.Request): string => {
		return encodeURI(
			`${url}?filename=${encodeURIComponent(request.fileName)}&documentModelName=${request.documentModelName}&pathToField=${request.pathToField}`
		);
	};
}

const EMPTY_ATTACHMENT: Attachment = {
	attachment_id: null,
	category: null,
	content: null,
	description: null,
	internal_filename: null,
	mime_type: null,
	original_filename: null,
	size: null
};
